import { useState, useEffect, useMemo, useCallback } from 'react';
import Api from '../../../../component/Api';

export function useSupervisorDetails() {
    const { http } = useMemo(() => Api(), []);

    const [supervisor, setSupervisor] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const [profileError, setProfileError] = useState(null);

    const [disciplines, setDisciplines] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [disciplinesLoading, setDisciplinesLoading] = useState(true);
    const [disciplinesError, setDisciplinesError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function fetchProfile() {
            setProfileLoading(true);
            setProfileError(null);
            try {
                const response = await http.get('/is/profileDetails');
                const data = response.data;
                if (cancelled) return;
                setSupervisor({
                    name: data.name ?? data.username ?? 'N/A',
                    college: data.college ?? data.collegeName ?? 'N/A',
                    email: data.email ?? 'N/A',
                });
            } catch (err) {
                if (!cancelled) {
                    setProfileError(
                        err?.response?.data?.message ?? err.message ?? 'Failed to load profile.'
                    );
                }
            } finally {
                if (!cancelled) setProfileLoading(false);
            }
        }

        fetchProfile();
        return () => { cancelled = true; };
        //eslint-disable-next-line
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function fetchDisciplines() {
            setDisciplinesLoading(true);
            setDisciplinesError(null);
            try {
                const response = await http.get('/is/profileDiscipline', {
                    params: {
                        page: currentPage,
                        per_page: perPage,
                        search: search,
                    },
                });
                const data = response.data;
                if (cancelled) return;

                setDisciplines(data.data ?? []);
                setCurrentPage(data.current_page ?? 1);
                setLastPage(data.last_page ?? 1);
                setTotal(data.total ?? 0);
                setPerPage(data.per_page ?? perPage);
            } catch (err) {
                if (!cancelled) {
                    setDisciplinesError(
                        err?.response?.data?.message ?? err.message ?? 'Failed to load disciplines.'
                    );
                }
            } finally {
                if (!cancelled) setDisciplinesLoading(false);
            }
        }

        fetchDisciplines();
        return () => { cancelled = true; };
        //eslint-disable-next-line
    }, [currentPage, search, perPage]);

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
    }, []);

    const handleSearchChange = useCallback((value) => {
        setSearch(value);
        setCurrentPage(1);
    }, []);

    const refetchDisciplines = useCallback(() => {
        setCurrentPage(prev => prev); 
        setSearch(prev => prev);
    }, []);

    return {
        supervisor,
        profileLoading,
        profileError,

        disciplines,
        currentPage,
        lastPage,
        total,
        perPage,
        search,
        disciplinesLoading,
        disciplinesError,

        handlePageChange,
        handleSearchChange,
        refetchDisciplines,
    };
}